// gloss:file

import { applyCaps } from '../shared/applyCaps';
import { compactRequest } from '../shared/compactRequest';
import { mergeKeys } from '../shared/mergeKeys';
import { readPage } from '../shared/readPage';
import { readPath } from '../shared/readPath';
import type { ByCursorOptions, PageWalk, SdkItems, WalkedCursorPage } from '../types';
import { RepeatedCursorError } from './repeatedCursorError';

const LABEL = 'iterateByCursor';

// gloss
const readNextCursor = (value: unknown): string | null =>
  value === undefined || value === null || value === '' ? null : String(value);

// gloss
export const iterateByCursor = <R, T = SdkItems<R>>(options: ByCursorOptions<R>): PageWalk<WalkedCursorPage<T>> => ({
  async *[Symbol.asyncIterator]() {
    const keys = mergeKeys('cursor', options.keys);
    const progress = { pages: 0, rows: 0 };
    let cursor = options.startCursor || null;
    const sent = new Set<string>();

    for (;;) {
      if (cursor !== null) sent.add(cursor);
      const request = compactRequest({ [keys.cursor]: cursor, [keys.pageSize]: options.pageSize });
      const { response, items } = await readPage<R, T, typeof request>(options, request, progress, LABEL, keys.data);
      const nextCursor = readNextCursor(readPath(response, keys.nextCursor));
      if (nextCursor !== null && sent.has(nextCursor)) {
        throw new RepeatedCursorError(LABEL, progress.pages, nextCursor);
      }

      const isCapped = nextCursor !== null && applyCaps(options, progress);
      yield { items, page: progress.pages, cursor, nextCursor };
      if (nextCursor === null || isCapped) return;
      cursor = nextCursor;
    }
  },
});
